import { useQuery, useMutation, useQueryClient, keepPreviousData, type Query, type QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { chatsApi } from '@/api/chats'
import { useTenantKey } from './useTenantKey'
import type { Chat, ChatAgentSelection } from '@/types'

export function useChats(projectId: string, search?: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'chats', search),
    queryFn: () => chatsApi.getAll(projectId, search),
    enabled: !!projectId,
    placeholderData: keepPreviousData,
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
    staleTime: 0,
  })
}

export function useChatMessages(projectId: string, chatId: string | null) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'chats', chatId!, 'messages'),
    queryFn: () => chatsApi.getMessages(projectId, chatId!),
    enabled: !!projectId && !!chatId,
    refetchInterval: chatId ? 4_000 : false,
    refetchOnWindowFocus: true,
  })
}

export function useChatContainerRequirement(projectId: string, chatId: string | null) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'chats', chatId!, 'container-requirement'),
    queryFn: () => chatsApi.getContainerRequirement(projectId, chatId!),
    enabled: !!projectId && !!chatId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  })
}

export function useCreateChat(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: () => chatsApi.create(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chats') })
    },
  })
}

export function useDeleteChat(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: (chatId: string) => chatsApi.delete(projectId, chatId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chats') })
    },
  })
}

const chatListFilter = (chatsKey: readonly unknown[]) => ({
  queryKey: chatsKey,
  predicate: (query: Query) => query.queryKey.length === chatsKey.length + 1 && Array.isArray(query.state.data),
})

export function applyChatAgentSelection(
  queryClient: QueryClient,
  chatsKey: readonly unknown[],
  chatId: string,
  selection: ChatAgentSelection,
) {
  queryClient.setQueriesData<Chat[]>(chatListFilter(chatsKey), (chats) =>
    Array.isArray(chats)
      ? chats.map((c) => (c.id === chatId
        ? { ...c, agentProviderName: selection.agentProvider, model: selection.model, effort: selection.effort }
        : c))
      : chats,
  )
}

export function useChatAgentOptions(projectId: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'chats', 'meta', 'agent-options'),
    queryFn: () => chatsApi.getAgentOptions(projectId),
    enabled: !!projectId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  })
}

export function useUpdateChatAgentSelection(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  const mutationKey = ['chat-agent-selection', projectId]

  return useMutation({
    mutationKey,
    mutationFn: ({ chatId, selection }: { chatId: string; selection: ChatAgentSelection }) =>
      chatsApi.updateAgentSelection(projectId, chatId, selection),
    scope: { id: `chat-agent-selection-${projectId}` },
    onMutate: async ({ chatId, selection }) => {
      const chatsKey = tenantKey('projects', projectId, 'chats')
      await queryClient.cancelQueries(chatListFilter(chatsKey))
      const previous = queryClient.getQueriesData<Chat[]>(chatListFilter(chatsKey))
      applyChatAgentSelection(queryClient, chatsKey, chatId, selection)
      return { previous }
    },
    onError: (_error, _variables, context) => {
      context?.previous.forEach(([key, chats]) => queryClient.setQueryData(key, chats))
      toast.error('Failed to save the AI provider selection for this chat.')
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey }) === 1) {
        queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chats') })
      }
    },
  })
}

export function useUpdateChatTitle(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()

  return useMutation({
    mutationFn: ({ chatId, title }: { chatId: string; title: string }) =>
      chatsApi.updateTitle(projectId, chatId, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'chats') })
    },
  })
}
